"use client";

import { FileTextIcon, Plus, Shield, ShieldAlert, AlertTriangle, Eye, Edit, Trash2, TrendingUp, ArrowDown, Clock } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { toast } from "@/app/_module/components/ui/use-toast";

import type { Policy } from "../types/lead-with-policies-type";
import { useDeletePolicy } from "../hooks/queries";
import { usePolicyGuardLeadAlerts } from "../hooks/policy-guard-queries";
import { CarrierLogo } from "@/app/_module/components/CarrierLogo";
import type { PolicyAlert } from "../types/policy-guard.types";

import CreateLeadPolicy from "./CreateLeadPolicy";
import { UpdatePolicyDialog } from "./UpdateLeadPolicy";

interface LeadPolicyDetailsProps {
  policies: Policy[];
  hasPolicies: boolean;
  policyCount: number;
  leadId?: string;
  leadName?: string;
  isPrimaryLead?: boolean;
  onStatusChange?: (leadId: string, newStatus: string) => Promise<void>;
  onPolicyUpdate?: () => void;
}

export const LeadPolicyDetails = function ({
  policies,
  hasPolicies,
  policyCount,
  leadId,
  leadName,
  isPrimaryLead = true,
  onStatusChange,
  onPolicyUpdate
}: LeadPolicyDetailsProps) {
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [policyToDelete, setPolicyToDelete] = useState<Policy | null>(null);

  const deletePolicy = useDeletePolicy();

  // PolicyGuard: fetch risk alerts for this lead's policies
  // Feature flag — set to true when ready to launch PolicyGuard
  const POLICY_GUARD_ENABLED = false;
  const { data: policyAlerts } = usePolicyGuardLeadAlerts(POLICY_GUARD_ENABLED ? (leadId || '') : '');
  const getAlertForPolicy = (policyId: string): PolicyAlert | undefined =>
    policyAlerts?.find(a => a.policyId === policyId);

  const policiesTitle = isPrimaryLead ? "Primary Lead Policies" : "Household Member Lead Policies";

  const handleDeletePolicy = async () => {
    if (!policyToDelete || !leadId) return;

    try {
      await deletePolicy.mutateAsync({
        policyId: policyToDelete.id,
        leadId,
      });

      toast({
        title: "Policy deleted",
        description: "The policy has been successfully deleted.",
      });

      // Check if this was the last policy
      const remainingPolicies = policies.filter(p => p.id !== policyToDelete.id);
      const isLastPolicy = remainingPolicies.length === 0;

      // If this was the last policy, reset lead status to "New lead"
      if (isLastPolicy && onStatusChange) {
        await onStatusChange(leadId, "New lead");
      }

      onPolicyUpdate?.();
      setPolicyToDelete(null);
    } catch (error) {
      console.error("Delete policy error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to delete policy. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  if (!hasPolicies || policies.length === 0) {
    return (
      <>
        <Card className="shadow-sm border-gray-200">
          <CardContent className="p-6">
            <div className="text-center py-8">
              <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Policies</h3>
              <p className="mt-1 text-sm text-gray-500">
                This lead doesn&apos;t have any policies yet.
              </p>
              {leadId && (
                <Button
                  className="mt-4"
                  onClick={() => setShowPolicyModal(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Policy Sale
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {leadId && (
          <CreateLeadPolicy
            isOpen={showPolicyModal}
            leadId={leadId}
            leadName={leadName}
            onOpenChange={setShowPolicyModal}
            onStatusUpdate={onStatusChange}
            onSave={() => {
              setShowPolicyModal(false);
            }}
          />
        )}
      </>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not specified";
    // Parse date safely - handle both ISO datetime and YYYY-MM-DD formats
    // For date-only fields, we want to show the date as stored, not shifted by timezone
    let year: number, month: number, day: number;

    if (dateString.includes('T')) {
      // ISO format like "2024-12-19T12:00:00.000Z" - extract date components from UTC
      const utcDate = new Date(dateString);
      year = utcDate.getUTCFullYear();
      month = utcDate.getUTCMonth();
      day = utcDate.getUTCDate();
    } else {
      // YYYY-MM-DD format
      [year, month, day] = dateString.split('-').map(Number);
      month = month - 1; // Convert to 0-indexed month
    }

    // Create date at noon local time to avoid any timezone edge cases when displaying
    const date = new Date(year, month, day, 12, 0, 0, 0);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "expired":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const getPolicyStatusLabel = (status: string) => {
    // Capitalize first letter of each word
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get the lead status label based on policy stage and status
  const getPolicyStageLabel = (policyStage?: string, policyStatus?: string) => {
    // If policy status is lapsed, show Lapsed
    if (policyStatus?.toLowerCase() === 'lapsed') {
      return 'Lapsed';
    }
    // If policy status is declined, show Declined
    if (policyStatus?.toLowerCase() === 'declined') {
      return 'Declined';
    }
    if (policyStage === 'sold') {
      return 'Sold & Approved';
    }
    return 'Underwriting';
  };

  // Get color for policy stage (maps to lead status colors from get-status-color.ts)
  const getPolicyStageColor = (policyStage?: string, policyStatus?: string) => {
    // If policy status is lapsed, show lapsed color
    if (policyStatus?.toLowerCase() === 'lapsed') {
      return 'bg-gray-900 text-gray-100'; // Lapsed color
    }
    // If policy status is declined, show declined color
    if (policyStatus?.toLowerCase() === 'declined') {
      return 'bg-gray-600 text-gray-100'; // Declined color
    }
    if (policyStage === 'sold') {
      return 'bg-emerald-600 text-emerald-100'; // Sold & Approved color
    }
    return 'bg-amber-500 text-amber-100'; // Underwriting color
  };

  const getPolicyStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'approved':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_review':
        return 'bg-blue-100 text-blue-800';
      case 'additional_info_needed':
        return 'bg-orange-100 text-orange-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'issued':
        return 'bg-green-100 text-green-800';
      case 'lapsed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-4 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Shield className="h-5 w-5 flex-shrink-0" />
            {/* count rides INSIDE the text span so it hugs the last word even
                when the title wraps — as a flex sibling it floated detached
                beside the two-line title */}
            <span className="min-w-0">
              {policiesTitle}
              <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 align-middle text-xs font-semibold text-gray-600">
                {policyCount}
              </span>
            </span>
          </div>
          {leadId && (
            <Button onClick={() => setShowPolicyModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Policy
            </Button>
          )}
        </CardHeader>
        <CardContent className="divide-y">
          {policies.map((policy) => (
            <div key={policy.id} className="@container space-y-3 py-4 first:pt-0 last:pb-0">
              {/* row 1: stage, actions; row 2: carrier (what agents call the policy
                  by), product + number; then the figures */}
              <div className="flex items-center gap-2">
                <Badge className={getPolicyStageColor((policy as any).policyStage, policy.status)}>
                  {getPolicyStageLabel((policy as any).policyStage, policy.status)}
                </Badge>
                {/* PolicyGuard Risk Badge */}
                {(() => {
                  const alert = getAlertForPolicy(policy.id);
                  if (!alert) return null;
                  const riskColors: Record<string, string> = {
                    critical: 'bg-red-600 text-white',
                    at_risk: 'bg-orange-500 text-white',
                    watch: 'bg-yellow-500 text-white',
                  };
                  const riskIcons: Record<string, React.ReactNode> = {
                    critical: <ShieldAlert className="h-3 w-3" />,
                    at_risk: <AlertTriangle className="h-3 w-3" />,
                    watch: <Eye className="h-3 w-3" />,
                  };
                  return (
                    <Badge className={`${riskColors[alert.riskLevel] || ''} text-[10px] px-1.5 py-0 gap-1`} title={alert.riskFactors?.map(f => f.detail).join(', ')}>
                      {riskIcons[alert.riskLevel]}
                      {alert.riskLevel === 'critical' ? 'Critical' : alert.riskLevel === 'at_risk' ? 'At Risk' : 'Watch'}
                    </Badge>
                  );
                })()}
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-auto h-8 w-8 text-gray-500 hover:text-gray-900"
                  onClick={() => setEditingPolicy(policy)}
                  aria-label="Edit policy"
                  title="Edit policy"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:bg-red-50 hover:text-red-600"
                  onClick={() => setPolicyToDelete(policy)}
                  aria-label="Delete policy"
                  title="Delete policy"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-start gap-3">
                <CarrierLogo name={policy.carrierName} size={36} />
                <div className="min-w-0">
                  {/* ! beats globals.css, which makes every h4 1.25rem below 767px */}
                  <h4 className="break-words !text-base font-semibold leading-snug text-gray-900">
                    {policy.carrierName}
                  </h4>
                  {/* the number wraps here instead of being cut off beside the actions */}
                  <p className="break-words text-sm text-gray-500">
                    {[policy.productType, policy.policyNumber && `#${policy.policyNumber}`].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>

              {/* three across once a column fits "Coverage Amount" / $1,000,000.00 (~116px), else two */}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm @sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-gray-500">Annual Premium</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-gray-900">
                    {formatCurrency(policy.annualPremium.toString())}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Coverage Amount</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-gray-900">
                    {formatCurrency(policy.premium.toString())}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Issued</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">{formatDate(policy.issuedAt)}</dd>
                  {/* under Issued, not a 4th cell: four don't fit, and a lone 4th wrapped onto its own row */}
                  {!!policy.expiryDate && (
                    <dd className="text-xs text-gray-500">
                      Renews <span className="whitespace-nowrap">{formatDate(policy.expiryDate)}</span>
                    </dd>
                  )}
                </div>
              </dl>

              {/* Commission status — joined from policy_commissions on the
                  backend. Hidden when the policy hasn't transitioned to a
                  status that produces a commission row yet. */}
              {policy.commission && (
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  {policy.commission.status === "advanced" && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm">
                        <TrendingUp className="h-4 w-4 text-emerald-600" />
                        <span className="text-gray-600">Commission advanced</span>
                        <span className="font-semibold text-emerald-700">
                          {formatCurrency(
                            String(
                              policy.commission.advancedAmount ??
                                policy.commission.expectedAmount ??
                                0,
                            ),
                          )}
                        </span>
                        {policy.commission.commissionRate && (
                          <span className="text-xs text-gray-400">
                            ({Math.round(policy.commission.commissionRate * 100)}%)
                          </span>
                        )}
                      </div>
                      {policy.commission.daysUntilEarned != null && (
                        <p className="text-xs text-gray-500 pl-6">
                          {policy.commission.daysUntilEarned} day
                          {policy.commission.daysUntilEarned !== 1 ? "s" : ""} until earned
                          {policy.commission.chargebackWindowDays
                            ? ` · ${policy.commission.chargebackWindowDays}-day chargeback window`
                            : ""}
                        </p>
                      )}
                    </div>
                  )}
                  {policy.commission.status === "earned" && (
                    <div className="flex items-center gap-2 text-sm">
                      <Shield className="h-4 w-4 text-emerald-600" />
                      <span className="text-gray-600">Earned</span>
                      <span className="font-semibold text-emerald-700">
                        {formatCurrency(
                          String(
                            policy.commission.advancedAmount ??
                              policy.commission.expectedAmount ??
                              0,
                          ),
                        )}
                      </span>
                      {policy.commission.earnedAt && (
                        <span className="text-xs text-gray-500">
                          · locked in {formatDate(policy.commission.earnedAt)}
                        </span>
                      )}
                    </div>
                  )}
                  {policy.commission.status === "charged_back" && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm">
                        <ArrowDown className="h-4 w-4 text-rose-600" />
                        <span className="text-gray-600">Charged back</span>
                        <span className="whitespace-nowrap font-semibold text-rose-700">
                          −
                          {formatCurrency(
                            String(
                              policy.commission.chargedBackAmount ??
                                policy.commission.advancedAmount ??
                                0,
                            ),
                          )}
                        </span>
                        {policy.commission.chargedBackAt && (
                          <span className="text-xs text-gray-500">
                            · {formatDate(policy.commission.chargedBackAt)}
                          </span>
                        )}
                      </div>
                      {policy.commission.chargedBackReason && (
                        <p className="text-xs text-rose-600/80 pl-6">
                          {policy.commission.chargedBackReason}
                        </p>
                      )}
                    </div>
                  )}
                  {policy.commission.status === "pending" && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <span className="text-gray-600">Projected commission</span>
                      <span className="font-semibold text-amber-700">
                        {formatCurrency(
                          String(policy.commission.expectedAmount ?? 0),
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {leadId && (
        <>
          <CreateLeadPolicy
            isOpen={showPolicyModal}
            leadId={leadId}
            leadName={leadName}
            onOpenChange={setShowPolicyModal}
            onStatusUpdate={onStatusChange}
            onSave={() => {
              setShowPolicyModal(false);
            }}
          />

          <UpdatePolicyDialog
            isOpen={!!editingPolicy}
            leadId={leadId}
            policy={editingPolicy ? policies.find(p => p.id === editingPolicy.id) || editingPolicy : null}
            onOpenChange={(open) => {
              if (!open) setEditingPolicy(null);
            }}
            onSave={(success) => {
              if (success) {
                onPolicyUpdate?.();
              }
              setEditingPolicy(null);
            }}
            onStatusChange={onStatusChange}
          />

          <AlertDialog open={!!policyToDelete} onOpenChange={(open) => !open && setPolicyToDelete(null)}>
            <ResponsiveAlertDialogContent size="md">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Policy</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this policy? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {policyToDelete && (
                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="font-medium text-gray-900">{policyToDelete.productType}</p>
                  <p className="text-sm text-gray-600">Policy #{policyToDelete.policyNumber}</p>
                </div>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeletePolicy}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </ResponsiveAlertDialogContent>
          </AlertDialog>
        </>
      )}
    </>
  );
};